import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Minus, Pause, Plus, Rabbit, Snail } from "lucide-react";

import {
    activeSegmentIndex,
    gongsBetween,
    paceScript,
    readingProgress,
    totalReadingSeconds,
    wordsDue,
} from "../../domain/prompter";
import {
    MAX_READING_SPEED_WPM,
    MIN_READING_SPEED_WPM,
} from "../../domain/readingSpeed";
import { formatDuration } from "../../domain/types";

/** The sizes the text steps through, smallest first. */
const TEXT_SIZES = ["text-base", "text-lg sm:text-xl", "text-2xl sm:text-3xl"] as const;
const SIZE_KEY = "alchely.meditation.prompterSize";

/**
 * How much one press changes the pace.
 *
 * Ten words a minute is a step you can hear in your own voice within a
 * sentence — small enough to correct with, large enough not to need pressing
 * six times while a patient waits.
 */
const PACE_STEP = 10;

/** The size this therapist last read at, or the middle one. */
function readStoredSize(): number {
    try {
        const stored = Number(localStorage.getItem(SIZE_KEY));
        if (Number.isInteger(stored) && stored >= 0 && stored < TEXT_SIZES.length) {
            return stored;
        }
    } catch {
        // A browser that refuses storage reads at the default size.
    }
    return 1;
}

type Props = {
    script: string;
    /** The pace the script was written to be read at. */
    wordsPerMinute: number;
    /** Seconds since the reading started. */
    elapsedSeconds: number;
    /** Put the marker back where the therapist actually is. */
    onSeekToSegment?: (startSeconds: number) => void;
    /**
     * Sound the room's singing bowl, where the script asked for one.
     *
     * Absent outside a call — nothing rings in an editor — and the marks are
     * still drawn, so a therapist writing one sees where it will land.
     */
    onGong?: () => void;
};

/**
 * The script, scrolling at the pace it is meant to be read.
 *
 * A therapist guiding a meditation has their hands free and their attention on
 * the patient, so the prompter has to keep its own place: the paragraph being
 * read is lit, the ones around it are dimmed but still legible, and the whole
 * thing scrolls itself so nothing is ever touched mid-sentence.
 *
 * It leads rather than commands. A therapist who has drifted — a patient
 * spoke, a breath ran long — taps the paragraph they are actually on and the
 * marker moves to them, instead of them chasing it. That is also why the
 * dimmed text stays readable: a prompter that hides everything but the current
 * line is unusable the moment you are not exactly where it thinks you are.
 *
 * The text can be made bigger or smaller, and the size is remembered. A
 * prompter is read from wherever the therapist sits — a laptop on a desk, a
 * screen across the room — and one size cannot be right for both.
 *
 * The pace can be changed here too, live, while the meditation runs. The pace
 * saved on the meditation is what its author thought it should be read at;
 * what it is actually being read at depends on the person in the room today —
 * a patient breathing longer, a silence that wants holding. Changing it moves
 * the marker back toward the therapist rather than making them chase it.
 *
 * The change lives as long as the reading does. It is not written back to the
 * meditation: adjusting for one session is not the same as deciding the script
 * was written wrong, and a control that quietly re-saved the author's work
 * would make every therapist afraid to touch it.
 */
export default function MeditationPrompter({
    script,
    wordsPerMinute,
    elapsedSeconds,
    onSeekToSegment,
    onGong,
}: Props) {
    const { t } = useTranslation();
    /*
     * The pace being read at, which starts as the pace it was written at. Re-
     * seeded when the meditation changes, so choosing another one in the same
     * call does not inherit the last one's correction.
     */
    const [pace, setPace] = useState(wordsPerMinute);
    useEffect(() => setPace(wordsPerMinute), [wordsPerMinute]);

    const segments = useMemo(() => paceScript(script, pace), [script, pace]);
    const activeIndex = activeSegmentIndex(segments, elapsedSeconds);
    const total = totalReadingSeconds(segments);
    const progress = readingProgress(segments, elapsedSeconds);

    const [size, setSize] = useState(readStoredSize);
    const resize = (step: number) => {
        const next = Math.min(TEXT_SIZES.length - 1, Math.max(0, size + step));
        setSize(next);
        try {
            localStorage.setItem(SIZE_KEY, String(next));
        } catch {
            // A browser that refuses storage still gets the bigger text; it
            // just asks again next session.
        }
    };

    const adjustPace = (step: number) =>
        setPace((current) =>
            Math.min(
                MAX_READING_SPEED_WPM,
                Math.max(MIN_READING_SPEED_WPM, current + step)
            )
        );

    /*
     * The strikes are sounded from the clock rather than from a segment
     * becoming active: a gong has no length, so it is never "the paragraph we
     * are on" — it is a moment the reading passes through.
     */
    const lastElapsed = useRef(elapsedSeconds);
    useEffect(() => {
        const from = lastElapsed.current;
        lastElapsed.current = elapsedSeconds;
        if (!onGong) return;
        // One strike per mark the reading passed, in the order it passed them.
        gongsBetween(segments, from, elapsedSeconds).forEach(() => onGong());
    }, [segments, elapsedSeconds, onGong]);

    const activeRef = useRef<HTMLLIElement | null>(null);
    useEffect(() => {
        // `nearest` rather than `center`: centring re-scrolls on every
        // paragraph even when the next one is already comfortably in view,
        // which reads as the text twitching under the therapist's eyes.
        activeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [activeIndex]);

    if (segments.length === 0) return null;

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex items-center gap-3 text-xs text-room-ink-muted">
                <span
                    className="h-1.5 flex-1 overflow-hidden rounded-full bg-room-line"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progress * 100)}
                    aria-label={t("meditation.prompter.progress")}
                >
                    <span
                        className="block h-full rounded-full bg-room-accent transition-[width] duration-300"
                        style={{ width: `${progress * 100}%` }}
                    />
                </span>
                <span className="tabular-nums">
                    {formatDuration(elapsedSeconds)} / {formatDuration(total)}
                </span>

                {/* Pace first, then size: one is about the reading, the other
                    about the eyes, and mixing four identical buttons into one
                    run would make both hard to find at a glance. */}
                <span className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => adjustPace(-PACE_STEP)}
                        disabled={pace <= MIN_READING_SPEED_WPM}
                        aria-label={t("meditation.prompter.slower")}
                        title={t("meditation.prompter.paceValue", { wpm: pace })}
                        className="flex size-8 items-center justify-center rounded-lg text-room-ink-muted hover:bg-room-active disabled:opacity-40"
                    >
                        <Snail size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={() => adjustPace(PACE_STEP)}
                        disabled={pace >= MAX_READING_SPEED_WPM}
                        aria-label={t("meditation.prompter.faster")}
                        title={t("meditation.prompter.paceValue", { wpm: pace })}
                        className="flex size-8 items-center justify-center rounded-lg text-room-ink-muted hover:bg-room-active disabled:opacity-40"
                    >
                        <Rabbit size={14} />
                    </button>
                </span>

                <span className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => resize(-1)}
                        disabled={size === 0}
                        aria-label={t("meditation.prompter.smaller")}
                        className="flex size-8 items-center justify-center rounded-lg text-room-ink-muted hover:bg-room-active disabled:opacity-40"
                    >
                        <Minus size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={() => resize(1)}
                        disabled={size === TEXT_SIZES.length - 1}
                        aria-label={t("meditation.prompter.bigger")}
                        className="flex size-8 items-center justify-center rounded-lg text-room-ink-muted hover:bg-room-active disabled:opacity-40"
                    >
                        <Plus size={14} />
                    </button>
                </span>
            </div>

            <ol
                className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-2"
                data-testid="meditation-prompter"
            >
                {segments.map((segment, index) => {
                    const isActive = index === activeIndex;
                    return (
                        <li
                            key={`${index}-${segment.startSeconds}`}
                            ref={isActive ? activeRef : undefined}
                            aria-current={isActive ? "step" : undefined}
                        >
                            <button
                                type="button"
                                onClick={() => onSeekToSegment?.(segment.startSeconds)}
                                className={`block w-full cursor-pointer whitespace-pre-line text-left leading-relaxed transition-colors ${TEXT_SIZES[size]} ${
                                    isActive
                                        ? // The paragraph carries the weight;
                                          // the words inside it carry the
                                          // colour, so the therapist reading
                                          // from a couple of feet away can see
                                          // which line they should be on
                                          // rather than only which block.
                                          "font-semibold"
                                        : "text-room-ink-muted"
                                }`}
                            >
                                {segment.kind === "gong" ? (
                                    <span className="inline-flex items-center gap-2 rounded-full bg-room-active px-3 py-1 text-sm text-room-ink-muted">
                                        <Bell size={14} aria-hidden="true" />
                                        {t("meditation.prompter.gong")}
                                    </span>
                                ) : segment.kind === "pause" ? (
                                    <Silence
                                        seconds={segment.pauseSeconds ?? 0}
                                        remaining={Math.max(
                                            0,
                                            Math.ceil(segment.endSeconds - elapsedSeconds)
                                        )}
                                        isActive={isActive}
                                    />
                                ) : isActive ? (
                                    <SpokenSoFar
                                        text={segment.text}
                                        said={wordsDue(segment, elapsedSeconds)}
                                    />
                                ) : (
                                    segment.text
                                )}
                            </button>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}

/**
 * The paragraph being read, coloured up to where the clock has got to.
 *
 * Splitting on whitespace and keeping the separators is what lets the line
 * breaks the therapist wrote survive: the tokens are re-emitted as they came,
 * so a break is still a break rather than a space.
 */
function SpokenSoFar({ text, said }: { text: string; said: number }) {
    const tokens = text.split(/(\s+)/);
    let word = 0;

    return (
        <>
            {tokens.map((token, index) => {
                if (/^\s+$/.test(token)) return <span key={index}>{token}</span>;
                word += 1;
                return (
                    <span
                        key={index}
                        className={
                            word <= said ? "text-room-accent-ink" : "text-room-ink"
                        }
                    >
                        {token}
                    </span>
                );
            })}
        </>
    );
}

/**
 * A silence the therapist wrote into the script.
 *
 * Drawn as a rest rather than as blank space, and counting down while it runs:
 * a prompter that simply stopped would read as a prompter that had broken, at
 * the exact moment the therapist most needs to trust it.
 */
function Silence({
    seconds,
    remaining,
    isActive,
}: {
    seconds: number;
    remaining: number;
    isActive: boolean;
}) {
    const { t } = useTranslation();

    return (
        <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm ${
                isActive
                    ? "bg-room-accent/20 text-room-accent-ink"
                    : "bg-room-active text-room-ink-muted"
            }`}
        >
            <Pause size={14} aria-hidden="true" />
            {isActive
                ? t("meditation.prompter.silenceLeft", { seconds: remaining })
                : t("meditation.prompter.silence", { seconds })}
        </span>
    );
}
